import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLibraryInfinite } from "@/api/hooks";
import type { LibraryItem } from "@/api/models/api-types";
import { useDebounce } from "@/hooks/use-debounce";
import { groupItemsByDate, type LibraryDateGroup } from "@/features/library/lib/date-groups";
import { getMediaKind } from "@/features/library/lib/library-media";
import { useFilePickerStore, usePickerRecentItems } from "../store/file-picker-store";

const PAGE_SIZE = 36;

const matchesCategory = (item: LibraryItem, category: string | null) => {
  if (!category) return true;
  const categories = (item.categories ?? []).map((value) => value.toLowerCase());
  if (category === "uncategorized") return categories.length === 0;
  return categories.includes(category.toLowerCase());
};

/**
 * Seçicinin listelediği öğeler. Kaynak, köken, arama ve filtreler store'dan okunur;
 * ızgara ve hızlı önizleme aynı parametrelerle çağırdığı için tek istek atılır.
 */
export const usePickerLibrary = () => {
  const { t, i18n } = useTranslation();
  const config = useFilePickerStore((state) => state.config);
  const source = useFilePickerStore((state) => state.source);
  const folderId = useFilePickerStore((state) => state.folderId);
  const origin = useFilePickerStore((state) => state.origin);
  const search = useFilePickerStore((state) => state.search);
  const typeFilter = useFilePickerStore((state) => state.typeFilter);
  const categoryFilter = useFilePickerStore((state) => state.categoryFilter);
  const recentItems = usePickerRecentItems();
  const debouncedSearch = useDebounce(search, 400);
  const isRecent = source === "recent";

  const query = useLibraryInfinite({
    limit: PAGE_SIZE,
    sortBy: "-createdAt",
    search: debouncedSearch || undefined,
    type: typeFilter ?? undefined,
    isInput: origin === "any" ? undefined : origin === "inputs",
    isWardrobe: config?.isWardrobe,
    tags: config?.libraryTags,
    isFavorite: source === "favorites" ? true : undefined,
    folderId: source === "folder" ? folderId ?? undefined : undefined,
  });

  const items = useMemo(() => {
    if (isRecent) {
      const term = debouncedSearch.trim().toLowerCase();
      return recentItems.filter(
        (item) =>
          (!typeFilter || getMediaKind(item) === typeFilter) &&
          matchesCategory(item, categoryFilter) &&
          (!term ||
            item.title?.toLowerCase().includes(term) ||
            item.prompt?.toLowerCase().includes(term))
      );
    }
    const loaded = query.data?.pages.flatMap((page) => page.result ?? []) ?? [];
    return loaded.filter((item) => matchesCategory(item, categoryFilter));
  }, [categoryFilter, debouncedSearch, isRecent, query.data, recentItems, typeFilter]);

  const groups = useMemo<LibraryDateGroup[]>(() => {
    // Son kullanılanlar tarih değil kullanım sırasıyla gösterilir
    if (isRecent) {
      return items.length > 0
        ? [{ key: "recent", label: t("filePicker.sources.recent"), items }]
        : [];
    }
    return groupItemsByDate(items, t, i18n.language);
  }, [i18n.language, isRecent, items, t]);

  return {
    items,
    groups,
    isRecent,
    isFiltered: !!debouncedSearch || !!typeFilter || !!categoryFilter || origin !== "any",
    isLoading: !isRecent && query.isLoading,
    isError: !isRecent && query.isError,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !isRecent && !!query.hasNextPage,
    isFetchingNextPage: !isRecent && query.isFetchingNextPage,
  };
};
